import React, { useCallback, useEffect, useState } from "react";

import { formatCurrency } from "@dehaat/dds";

import SectionLayout from "../../components/reusable/SectionLayout";
import fetchFarmerOrders from "../../apis/fetchFarmerOrders";
import { Order, OrderAggregate } from "../../models/orders";

import OrderListingCard from "./OrderListingCard";
import EmptySectionCard from "../reusable/EmtySectionCard";
import { rnNavigation } from "app/utils/appConfig";
import { ROUTES as FARMER_360_ROUTES, ROUTES } from "../../routes/types";

interface Props {
  farmerId: string;
  highlight: boolean;
}

const OrdersSection = ({ farmerId, highlight }: Props) => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [aggregate, setAggregate] = useState<OrderAggregate>();
  const [loading, setLoading] = useState(true);

  const fetchFarmerOrdersAndSet = useCallback(async () => {
    const response = await fetchFarmerOrders({ farmerId });

    if (response) {
      setOrders(response.results);
      setAggregate(response.orders_aggregate);
    }
  }, [farmerId]);

  const getDescription = () => {
    if (loading || !aggregate) {
      return "";
    }
    const { total_count, duration, total_amount } = aggregate;
    return `Number of orders: ${total_count}, \nTotal purchase in last ${duration}: ${formatCurrency(
      total_amount
    )}/-`;
  };

  useEffect(() => {
    fetchFarmerOrdersAndSet().finally(() => setLoading(false));
  }, [fetchFarmerOrdersAndSet]);

  return (
    <SectionLayout
      title="Orders"
      description={getDescription()}
      loading={loading}
      onViewAllClick={() =>
        rnNavigation({
          name: FARMER_360_ROUTES.ORDERS_LISTING,
          params: { farmerId },
        })
      }
      highlight={highlight}
      isViewAllCTADisabled={orders.length < 10}
    >
      {orders.map((order) => (
        <OrderListingCard
          key={order.id}
          orderId={order.id}
          date={order.created_at}
          price={order.grand_total}
          name={order.buyer_full_name}
          orderLines={order.order_lines}
          status={order.status}
          onClick={() =>
            rnNavigation({
              name: ROUTES.ORDER_DETAILS,
              params: { orderId: order.id },
            })
          }
        />
      ))}
      {orders.length === 0 && <EmptySectionCard title="No order history" />}
    </SectionLayout>
  );
};

export default OrdersSection;
